/* Component  */
import { getFieldData } from "../utils/FieldHelpers";
import {
  compare,
  distinct,
  email,
  isInvalidBoundarySpaces,
  isOnlyText,
  isUnique,
  isValidEIN,
  isValidFax,
  isvalidPassword,
  isValidPhone,
  isValidZipCode,
  isWebsite,
  maxLength,
  maxProspects,
  maxSum,
  minEndDate,
  minLength,
  number, required, uniqueIdentifier,
  validateCharacters,
  validateFile,
  validateImageFile,
  apaarId,
  isGradePointFormat,
  isValidMaskFormat,
  validateMaxDate,
  validateMinDate
} from './ValidateField';


// Validation functions
export function ValidateAll(state, rules, section) {
  let result = {
    isValid: true,
    error: {}
  }
  const keys = Object.keys(rules);
  let error = {};
  keys.forEach(key => {
    const fieldRules = rules[key];
    const formField = getFieldData(section, key);
    // A field hidden by its visibleWhen is not shown, so it can't be filled: skip its rules
    if (typeof formField?.visibleWhen === "function" && !formField.visibleWhen(state)) return;
    const validateResult = ValidateField(state[key], fieldRules, state, formField);
    if (!validateResult.isvalid) {
      result.isValid = false;
      error[key] = validateResult.message;
    }
  });
  result.error = error;
  return result;
};


export function Validate(state, rules, key) {
  let result = {
    isValid: true,
    error: {}
  }
  let error = {};
  let fieldRules = rules[key]
  let validateResult = ValidateField(state[key], fieldRules, state);
  if (!validateResult.isvalid) {
    result.isValid = false;
    error[key] = validateResult.message;
  }
  result.error = error;
  return result;
};

// Form value of an image field ([{ file, image, ... }], { file, image, ... } or a saved url) -> the newly
// picked File + its loaded <img> (no File for a saved image — nothing new to check)
const getPickedImage = (value) => {
  const item = Array.isArray(value) ? value[0] : value;
  if (item instanceof File) return { file: item, imageData: {} };
  return { file: item?.file instanceof File ? item.file : null, imageData: item?.image ?? {} };
};

export function ValidateField(value, fieldRules, state, formField) {
  const result = { isvalid: true, message: '' };
  for (const rule of fieldRules) {
    if (!result.isvalid) break; // Exit early if already invalid
    const validator = getValidator(rule.type);
    // The image validator takes (value, fieldSetting, imageData) — not the common (value, rule, state, formField)
    // (without the field config there are no limits to check against, so it is skipped)
    const { file: pickedImage, imageData } = rule.type === "image" && formField ? getPickedImage(value) : {};
    const validationResponse = validator && (rule.type === "image"
      ? validator(pickedImage, formField?.fieldSetting ?? {}, imageData)
      : validator(value, rule, state, formField));
    // file / image validators return [{ isValid, errorMessage }] — an array is always truthy, so check its items
    if (Array.isArray(validationResponse)) {
      const failed = validationResponse.find((item) => !item.isValid);
      if (failed) {
        result.isvalid = false;
        result.message = rule.message || failed.errorMessage;
      }
      continue;
    }
    if (validator && !validationResponse) {
      result.isvalid = false;
      result.message = rule.message;
    } else if (validationResponse?.customMessage) {
      result.isvalid = validationResponse?.isValid;
      result.message = validationResponse?.customMessage;
    }
  }
  return result;
};


export const ValidateFileField = (value, fieldRules, state, acceptedFileFormats, minFileSize, maxFileSize) => {
  const result = { isValid: true, messages: [] };
  for (const rule of fieldRules) {
    if (!result.isValid) break; // Exit early if already invalid
    const validator = getValidator(rule.type);
    if (rule.type === "file" && validator) {
      const formField = { acceptedFormats: acceptedFileFormats, minFileSizeInMB: minFileSize, maxFileSizeInMB: maxFileSize }
      const validatorResults = validator(value, "", "", formField)
      for (const validatorResult of validatorResults) {
        if (!validatorResult.isValid) {
          result.isValid = false;
          result.messages.push({
            message: rule.message || validatorResult.errorMessage,
            // fileName: validatorResult.fileName
          });
        }
      }
    }
  }
  return result;
}

export const ValidateImageField = (value, fieldRules, state, fieldSetting, imageData) => {
  const result = { isValid: true, messages: [] };
  for (const rule of fieldRules) {
    if (!result.isValid) break; // Exit early if already invalid
    const validator = getValidator(rule.type);
    if (rule.type === "image" && validator) {
      const validatorResults = validator(value, fieldSetting, imageData)
      for (const validatorResult of validatorResults) {
        if (!validatorResult.isValid) {
          result.isValid = false;
          result.messages.push({
            message: rule.message || validatorResult.errorMessage,
            // fileName: validatorResult.fileName
          });
        }
      }
    }
  }
  return result;
};

const validators = {
  require: (value) => required(value),
  email: (value) => email(value),
  password: (value) => isvalidPassword(value),
  compare: (value, rule, state) => compare(value, state[rule.compareEle]),
  distinct: (value, rule, state) => distinct(value, state[rule.distinctEle]),
  number: (value) => number(value),
  uniqueIdentifier: (value) => uniqueIdentifier(value),
  maxLength: (value, rule) => maxLength(value, rule.maxLength),
  minLength: (value, rule) => minLength(value, rule.minLength),
  maxProspects: (value, rule) => maxProspects(value, rule.maxProspects),
  minEndDate: (value, rule) => minEndDate(value, rule.minEndDate),
  maxSum: (value, rule) => maxSum(value, rule.maxSum),
  validEIN: (value) => isValidEIN(value),
  validPhone: (value) => isValidPhone(value),
  validFax: (value) => isValidFax(value),
  uniqueName: (value) => isUnique(value),
  invalidBoundarySpaces: (value) => isInvalidBoundarySpaces(value),
  website: (value) => isWebsite(value),
  onlyText: (value) => isOnlyText(value),
  validZipCode: (value, rule, state) => isValidZipCode(rule, state),
  validateCharacters: (value) => validateCharacters(value),
  apaarId: (value, rule) => apaarId(value, rule.length),
  file: (value, rule, state, formField) => {
    const validationResult = validateFile(value, formField)
    return [validationResult];
  },
  image: (value, fieldSetting, imageData) => {
    const validationResult = validateImageFile(value, fieldSetting, imageData)
    return [validationResult];
  },
  gradingPoint: (value, rule) => isGradePointFormat(value, rule?.gradePointFormat, rule?.maxValue),
  maxDate: (value, rule, state) => validateMaxDate(value, rule, state),
  minDate: (value, rule, state) => validateMinDate(value, rule, state),
  maskFormat: (value, rule) => isValidMaskFormat(value, rule?.format),
};

const getValidator = (type) => {
  return validators[type] || null;
};
